// client/src/components/common/StatCard.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

/**
 * Универсальная карточка статистики.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {React.ReactNode} props.value
 * @param {string} [props.subtitle]
 * @param {string} [props.color='primary']
 * @param {React.ReactNode} [props.icon]
 * @param {boolean} [props.loading=false]
 * @param {boolean} [props.compact=false] — компактный режим (для шапок)
 */
export default function StatCard({
  title,
  value,
  subtitle,
  color = 'primary',
  icon,
  loading = false,
  compact = false,
}) {
  if (compact) {
    return (
      <Card variant="outlined" sx={{ height: '100%', borderRadius: 1 }}>
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            p: 0.75,
            '&:last-child': { pb: 0.75 },
          }}
        >
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              {icon && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {React.cloneElement(icon, {
                    sx: { fontSize: 20, color: `${color}.main` },
                  })}
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  color={`${color}.main`}
                  fontWeight="bold"
                  sx={{ lineHeight: 1, fontSize: '1rem' }}
                >
                  {value}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: '0.6rem', lineHeight: 1.1 }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block', fontSize: '0.55rem', lineHeight: 1.1 }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', p: 2 }}>
        {loading ? (
          <CircularProgress size={40} />
        ) : (
          <>
            {icon && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                {React.cloneElement(icon, {
                  sx: { fontSize: 40, color: `${color}.main` },
                })}
              </Box>
            )}
            <Typography
              variant="h4"
              color={`${color}.main`}
              fontWeight="bold"
              gutterBottom
            >
              {value}
            </Typography>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
